import { Link, NavLink } from 'react-router-dom';
import { Menu, X, Search, Mail, Phone, MapPin, ChevronDown } from 'lucide-react';
import { SiFacebook, SiLinkedin, SiX } from 'react-icons/si';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ROUTE_PATHS } from '@/lib/index';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const navigationItems = [
    { name: 'Home', path: ROUTE_PATHS.HOME },
    { name: 'Suppliers', path: ROUTE_PATHS.SUPPLIERS },
    { name: 'Products', path: ROUTE_PATHS.PRODUCTS },
    { name: 'News', path: ROUTE_PATHS.NEWS },
    { name: 'Contact', path: ROUTE_PATHS.CONTACT },
  ];

  const footerLinks = {
    company: [
      { name: 'About Us', path: '#' },
      { name: 'Our Team', path: '#' },
      { name: 'Careers', path: '#' },
      { name: 'Press', path: '#' },
    ],
    resources: [
      { name: 'Supplier Directory', path: ROUTE_PATHS.SUPPLIERS },
      { name: 'Product Catalog', path: ROUTE_PATHS.PRODUCTS },
      { name: 'Industry News', path: ROUTE_PATHS.NEWS },
      { name: 'Events', path: '#' },
    ],
    support: [
      { name: 'Help Center', path: '#' },
      { name: 'Contact Us', path: ROUTE_PATHS.CONTACT },
      { name: 'Advertise', path: '#' },
      { name: 'List Your Company', path: '#' },
    ],
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4">
          <div className="flex h-16 items-center justify-between">
            <Link to={ROUTE_PATHS.HOME} className="flex items-center space-x-2">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary text-primary-foreground font-bold text-xl">
                AS
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-lg leading-none">Airport Security</span>
                <span className="text-xs text-muted-foreground">.asia</span>
              </div>
            </Link>

            <nav className="hidden md:flex items-center space-x-1">
              {navigationItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-foreground hover:bg-accent hover:text-accent-foreground'
                    }`
                  }
                >
                  {item.name}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSearchOpen(!searchOpen)}
                className="hidden md:flex"
              >
                <Search className="h-5 w-5" />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>

          <AnimatePresence>
            {searchOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="py-4 border-t border-border">
                  <div className="flex items-center space-x-2">
                    <Input
                      type="search"
                      placeholder="Search suppliers, products, news..."
                      className="flex-1"
                    />
                    <Button>Search</Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-border overflow-hidden"
            >
              <nav className="container mx-auto px-4 py-4 flex flex-col space-y-2">
                {navigationItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-foreground hover:bg-accent hover:text-accent-foreground'
                      }`
                    }
                  >
                    {item.name}
                  </NavLink>
                ))}
                <div className="pt-2">
                  <Input
                    type="search"
                    placeholder="Search..."
                    className="w-full"
                  />
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="bg-card border-t border-border">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-2">
              <Link to={ROUTE_PATHS.HOME} className="flex items-center space-x-2 mb-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary text-primary-foreground font-bold text-xl">
                  AS
                </div>
                <div className="flex flex-col">
                  <span className="font-bold text-lg leading-none">Airport Security</span>
                  <span className="text-xs text-muted-foreground">.asia</span>
                </div>
              </Link>
              <p className="text-sm text-muted-foreground mb-4 max-w-sm">
                Your premier B2B marketplace connecting airport security suppliers with industry professionals across Asia and beyond.
              </p>
              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4" />
                  <span>info@airportsecurity.asia</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4" />
                  <span>+65 6123 4567</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MapPin className="h-4 w-4" />
                  <span>Singapore</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Company</h3>
              <ul className="space-y-2">
                {footerLinks.company.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.path}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Resources</h3>
              <ul className="space-y-2">
                {footerLinks.resources.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.path}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <ul className="space-y-2">
                {footerLinks.support.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.path}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-border">
            <div className="bg-muted rounded-lg p-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold mb-1">Stay Updated</h3>
                  <p className="text-sm text-muted-foreground">
                    Subscribe to our newsletter for industry insights and updates
                  </p>
                </div>
                <div className="flex w-full md:w-auto space-x-2">
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    className="md:w-64"
                  />
                  <Button>Subscribe</Button>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              © 2026 Airport Security Asia. All rights reserved.
            </p>
            <div className="flex items-center space-x-4">
              <a
                href="#"
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Facebook"
              >
                <SiFacebook className="h-5 w-5" />
              </a>
              <a
                href="#"
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="X"
              >
                <SiX className="h-5 w-5" />
              </a>
              <a
                href="#"
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="LinkedIn"
              >
                <SiLinkedin className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
